import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';
import { Link } from 'react-router-dom';
import { History, Printer } from 'lucide-react';

type Order = Database['public']['Tables']['orders']['Row'] & {
  display_id?: number;
  delivery_date?: string;
  order_items: Array<{
    quantity: number;
    unit_price: number;
    price_at_time?: number;
    products: {
      name: string;
    };
  }>;
};

type OrderedProduct = {
  name: string;
  quantity: number;
  sum: number;
};

type OrderedProductsByDate = {
  [date: string]: {
    products: OrderedProduct[];
    totalSum: number;
  };
};

export function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = React.useState<Order[]>([]);
  const [orderedProductsByDate, setOrderedProductsByDate] = React.useState<OrderedProductsByDate>({});
  const [loading, setLoading] = React.useState(true);
  const [selectedDate, setSelectedDate] = React.useState<string | null>(null);
  const [showPickingList, setShowPickingList] = React.useState(false);
  const pickingListRef = React.useRef<HTMLDivElement>(null);
  const [producer, setProducer] = React.useState<{ company_name: string } | null>(null);

  // Function to handle printing of the picking list
  const handlePrintPickingList = () => {
    const printContent = document.getElementById('picking-list');
    if (!printContent) return;
    
    const originalBodyContent = document.body.innerHTML;
    document.body.innerHTML = printContent.innerHTML;
    
    window.print();
    
    document.body.innerHTML = originalBodyContent;
    window.location.reload(); // Reload to restore all event handlers
  };

  React.useEffect(() => {
    // If no user is logged in, exit early
    if (!user) {
      setLoading(false);
      return;
    }

    const userId = user.id;
    if (!userId) {
      setLoading(false);
      return;
    }

    // Fetch producer information
    const fetchProducer = async () => {
      try {
        const { data, error } = await supabase
          .from('producers')
          .select('company_name')
          .eq('id', userId)
          .single();
        
        if (error) throw error;
        setProducer(data);
      } catch (err) {
        console.error('Error fetching producer:', err);
      }
    };

    async function fetchOrders() {
      try {
        // First get all producer's products
        const { data: products, error: productsError } = await supabase
          .from('products')
          .select('id')
          .eq('producer_id', userId);

        if (productsError) throw productsError;
        if (!products?.length) {
          setOrders([]);
          setLoading(false);
          return;
        }

        // Then get order items for these products
        const { data: orderItems, error: orderItemsError } = await supabase
          .from('order_items')
          .select(`
            order_id,
            quantity,
            unit_price,
            price_at_time,
            products (
              name
            )
          `)
          .in('product_id', products.map(p => p.id));

        if (orderItemsError) throw orderItemsError;
        if (!orderItems?.length) {
          setOrders([]);
          setLoading(false);
          return;
        }

        // Get only upcoming orders (delivery date is today or in the future)
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to beginning of day for accurate comparison
        const todayISOString = today.toISOString();

        const { data: orders, error: ordersError } = await supabase
          .from('orders')
          .select('id, display_id, created_at, delivery_date, status, total_amount')
          .in('id', orderItems.map(item => item.order_id))
          .gte('delivery_date', todayISOString) // Only get current and future orders
          .order('delivery_date', { ascending: true });

        if (ordersError) throw ordersError;

        // Combine the data
        const ordersWithItems = orders.map(order => ({
          ...order,
          order_items: orderItems.filter(item => item.order_id === order.id)
        }));

        setOrders(ordersWithItems);

        // Process ordered products by date
        const productsByDate: OrderedProductsByDate = {};
        
        ordersWithItems.forEach(order => {
          // Use delivery_date if available, otherwise fallback to created_at
          const dateToUse = order.delivery_date || order.created_at;
          // Format the date using Swedish locale
          const date = new Date(dateToUse).toLocaleDateString('sv-SE');
          
          if (!productsByDate[date]) {
            productsByDate[date] = {
              products: [],
              totalSum: 0
            };
          }
          
          order.order_items.forEach(item => {
            const existingProduct = productsByDate[date].products.find(
              p => p.name === item.products.name
            );
            
            // Use price_at_time if available (for weight-based products) or fall back to unit_price
            const priceToUse = item.price_at_time || item.unit_price;
            const itemSum = item.quantity * priceToUse;
            
            if (existingProduct) {
              existingProduct.quantity += item.quantity;
              existingProduct.sum += itemSum;
            } else {
              productsByDate[date].products.push({
                name: item.products.name,
                quantity: item.quantity,
                sum: itemSum
              });
            }
            
            productsByDate[date].totalSum += itemSum;
          });
        });
        
        setOrderedProductsByDate(productsByDate);
      } catch (err) {
        console.error('Error fetching orders:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchProducer();
    fetchOrders();
  }, [user]);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-4 shadow rounded-lg">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="mt-4 h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Kommande leveranser</h1>
        <div className="flex space-x-2">
          {Object.keys(orderedProductsByDate).length > 0 && (
            <button
              onClick={() => setShowPickingList(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Printer className="h-4 w-4 mr-2" />
              Plocklista
            </button>
          )}
          <Link 
            to="/producer/orders/history" 
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <History className="h-4 w-4 mr-2" />
            Orderhistorik
          </Link>
        </div>
      </div>
      
      {/* Picking List Modal */}
      {showPickingList && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Välj leveransdatum för plocklista</h2>
              <button 
                onClick={() => setShowPickingList(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {Object.entries(orderedProductsByDate)
                .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
                .map(([date]) => (
                  <button
                    key={date}
                    onClick={() => setSelectedDate(date)}
                    className={`p-4 border rounded-md text-left ${
                      selectedDate === date 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium">Leverans: {date}</span>
                  </button>
                ))}
            </div>
            
            {selectedDate && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">Plocklista för leverans {selectedDate}</h3>
                  <button
                    onClick={handlePrintPickingList}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-700"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Skriv ut
                  </button>
                </div>
                
                <div id="picking-list" ref={pickingListRef} className="bg-white p-6">
                  <style type="text/css" media="print">
                    {`
                      @page { size: A4; margin: 1cm; }
                      body { font-family: sans-serif; }
                      table { width: 100%; border-collapse: collapse; }
                      th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                      th { background-color: #f2f2f2; }
                      h1, h2 { margin-bottom: 10px; }
                      .checkbox { width: 20px; height: 20px; border: 1px solid #000; display: inline-block; margin-right: 10px; }
                    `}
                  </style>
                  <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>
                    Plocklista - {selectedDate}
                  </h1>
                  <p style={{ marginBottom: '16px' }}>
                    Producent: {producer?.company_name || user?.email}
                  </p>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: '12px', backgroundColor: '#f2f2f2', textAlign: 'left' }}>✓</th>
                        <th style={{ border: '1px solid #ddd', padding: '12px', backgroundColor: '#f2f2f2', textAlign: 'left' }}>Produkt</th>
                        <th style={{ border: '1px solid #ddd', padding: '12px', backgroundColor: '#f2f2f2', textAlign: 'left' }}>Antal</th>
                        <th style={{ border: '1px solid #ddd', padding: '12px', backgroundColor: '#f2f2f2', textAlign: 'left' }}>Noteringar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderedProductsByDate[selectedDate].products
                        .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
                        .map((product, index) => (
                          <tr key={index}>
                            <td style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'center' }}>
                              <div style={{ width: '20px', height: '20px', border: '1px solid #000', display: 'inline-block' }}></div>
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '12px' }}>{product.name}</td>
                            <td style={{ border: '1px solid #ddd', padding: '12px' }}>{product.quantity}</td>
                            <td style={{ border: '1px solid #ddd', padding: '12px' }}></td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowPickingList(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md shadow-sm hover:bg-gray-200"
              >
                Stäng
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Ordered Products by Date - Upcoming */}
      {Object.keys(orderedProductsByDate).length > 0 ? (
        <div className="mt-8">
          {Object.entries(orderedProductsByDate)
            .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
            .map(([date, data]) => (
              <div key={date} className="mb-8 bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h2 className="text-lg font-medium text-gray-900">Leveransdatum: {date}</h2>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    Totalt ordervärde: {(data.totalSum / 1.12).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr
                  </p>
                </div>
                <div className="flow-root">
                  <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead>
                          <tr>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                              Produkt
                            </th>
                            <th scope="col" className="px-6 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Antal
                            </th>
                            <th scope="col" className="px-6 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Summa (ex moms)
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {data.products
                            .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
                            .map((product, index) => (
                            <tr key={index}>
                              <td className="whitespace-nowrap py-4 pl-6 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                {product.name}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                {product.quantity}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                {(product.sum / 1.12).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white shadow rounded-lg mt-6">
          <h3 className="mt-2 text-sm font-medium text-gray-900">Inga kommande leveranser</h3>
          <p className="mt-1 text-sm text-gray-500">
            Kommande leveranser visas här när de finns bokade.
          </p>
        </div>
      )}
    </div>
  );
}